import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Link,
  useHistory,
  useLocation,
} from 'react-router-dom';
import {
  Input,
  UncontrolledTooltip,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import { switchToUser } from '../../../actions/Auth';

import AdminUsersList2FAIcon from './2FAIcon';
import DateTime from '../../DateTime';

import { userIsAdmin } from '../../../helpers/Auth';
import { getFormattedDateTime } from '../../../helpers/DateTime';
import { getCurrentURLParams } from '../../../helpers/URLParams';
import { getBackButtonMeta } from '../../../helpers/App';

const AdminUsersListItem = ({
  user,
  selectedUsers,
  loggedInUser,
  handleDeactivate,
  handleActivate,
  handleSelectUser,
  handleSendWelcomeEmails,
}) => {
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const fileTo = {
    pathname: `/admin/settings/users/${user.uuid}/edit`,
    state: {
      goBackToListButton: {
        ...getBackButtonMeta('users'),
        search: getCurrentURLParams(location),
      },
    },
  };

  return (
    <tr>
      <td className="selector">
        <Input
          type="checkbox"
          name="selected-user"
          value={user.uuid}
          checked={selectedUsers.some(u => u.uuid === user.uuid)}
          onChange={handleSelectUser}
        />
      </td>
      <th scope="row" className={`user-name${user.deactivated_at ? ' inactive' : ''}`}>
        <Link to={fileTo} id={`user-name-link-${user.uuid}`}>
          {user.avatar_url ? (
            <div className="user-avatar">
              <img src={user.avatar_url} alt={user.name} className="avatar avatar-40" />
            </div>
          ) : (
            <div className="user-avatar">
              <div className="avatar avatar-40 icon-avatar gray">
                <i className="fas fa-user" />
              </div>
            </div>
          )}
          <div className="user-name-container">
            {user.deactivated_at && (
            <>
              <i className="fas fa-ban" />{' '}
              <UncontrolledTooltip placement="bottom" target={`user-name-link-${user.uuid}`}>
                {intl.formatMessage({ defaultMessage: 'This account is inactive' })}
              </UncontrolledTooltip>
            </>
            )}
            {(!user.deactivated_at && !user.last_login_at) && (
            <>
              <i className="fas fa-times" />{' '}
              <UncontrolledTooltip placement="bottom" target={`user-name-link-${user.uuid}`}>
                {user.activation_email_sent_at ? (
                  <>
                    {intl.formatMessage({
                      defaultMessage: 'This account has not been activated by the user. A welcome email was sent on {date}',
                    }, { date: getFormattedDateTime(user.activation_email_sent_at) })}
                  </>
                ) : (
                  <>
                    {intl.formatMessage({
                      defaultMessage: 'This account has not been activated. Send the welcome email to activate.',
                    })}
                  </>
                )}
              </UncontrolledTooltip>
            </>
            )}
            {user.name}
            {loggedInUser.client.workspace_owner?.uuid === user.uuid && (
            <>
              {' '}
              <i id="user-workspace-owner" className="fa-regular fa-crown" />
              <UncontrolledTooltip
                target="user-workspace-owner"
                boundariesElement="viewport"
                fade={false}
              >
                {intl.formatMessage({ defaultMessage: 'Workspace owner' })}
              </UncontrolledTooltip>
            </>
            )}
            {user.activated_by && (
            <span className="user-activated-by">
              {intl.formatMessage(
                { defaultMessage: 'Approved by {name}' },
                { name: user.activated_by.name },
              )}
            </span>
            )}
            {user.hidden && (
            <span className="user-hidden">
              {intl.formatMessage({ defaultMessage: 'Hidden' })}
            </span>
            )}
          </div>
        </Link>
        <AdminUsersList2FAIcon user={user} />
      </th>
      <td className="user-email">
        {user.email}
      </td>
      <td className="user-role">
        {user.role_name}
      </td>
      <td className="user-registered">
        <DateTime
          id={`user-created-${user.uuid}`}
          dateTime={user.created_at}
        />
      </td>
      <td className="action-more">
        <UncontrolledDropdown>
          <DropdownToggle nav className="options">
            <i className="far fa-ellipsis-h" />
          </DropdownToggle>
          <DropdownMenu right container=".main-container">
            <DropdownItem onClick={() => { history.push(`/profile/${user.uuid}`); }}>
              <i className="fas fa-user" />
              {intl.formatMessage({ defaultMessage: 'View profile' })}
            </DropdownItem>
            <Link to={fileTo} className="dropdown-item-link">
              <DropdownItem tag="span">
                <i className="fas fa-pencil" />
                {intl.formatMessage({ defaultMessage: 'Edit' })}
              </DropdownItem>
            </Link>
            {loggedInUser.uuid !== user.uuid && (
              <>
                {userIsAdmin() && (
                  <DropdownItem onClick={() => { switchToUser(user.uuid); }}>
                    <i className="fas fa-repeat-alt" />
                    {intl.formatMessage({ defaultMessage: 'Switch to user' })}
                  </DropdownItem>
                )}
                <DropdownItem divider />
                <DropdownItem
                  onClick={() => {
                    if (user.deactivated_at) {
                      handleActivate([user]);
                    } else {
                      handleDeactivate([user]);
                    }
                  }}
                >
                  <i className="fas fa-ban" />
                  {
                    user.deactivated_at ?
                      intl.formatMessage({ defaultMessage: 'Re-activate' }) :
                      intl.formatMessage({ defaultMessage: 'Deactivate' })
                  }
                </DropdownItem>
                {!user.last_login_at && (
                  <DropdownItem
                    onClick={() => {
                      handleSendWelcomeEmails([user.uuid]);
                    }}
                  >
                    <i className="fas fa-envelope" />
                    {user.activation_email_sent_at ? (
                      <span>{intl.formatMessage({ defaultMessage: 'Re-send activation email' })}</span>
                    ) : (
                      <span>{intl.formatMessage({ defaultMessage: 'Send activation email' })}</span>
                    )}
                  </DropdownItem>
                )}
              </>
            )}
          </DropdownMenu>
        </UncontrolledDropdown>
      </td>
    </tr>
  );
};

const mapStateToProps = state => ({
  loggedInUser: state.Auth.profile,
});

AdminUsersListItem.propTypes = {
  loggedInUser: PropTypes.object.isRequired,
  selectedUsers: PropTypes.array.isRequired,
  user: PropTypes.object.isRequired,
  handleDeactivate: PropTypes.func.isRequired,
  handleActivate: PropTypes.func.isRequired,
  handleSelectUser: PropTypes.func.isRequired,
  handleSendWelcomeEmails: PropTypes.func.isRequired,
};

export default connect(mapStateToProps)(AdminUsersListItem);
